<script lang="ts">
    import {Flex, Spacer} from 'shared/layouts';
    import {KpGenericTopbar} from 'shared/components/kp-generic-topbar';
    import {LabeledValue} from 'src/features/erp-sutor/shared';
    import {ErpTwoSplitLayout} from 'src/features/erp/components';
    import type {RecordRow} from 'src/features/record-grid/lib/types';
    import type {PairUnpairEventData} from './types';
    import type {SearchManagerConnectableProxy} from 'src/features/search/search-manager/search-manager-connectable-proxy';
    import {getProjectLogbookPairingContext} from './project-logbook-pairing-context';
    import {getFirstFieldByFieldTypeIdRecursive} from 'src/features/record-grid/lib/grid-fields';
    import {exists} from 'shared/utils/custom-utils';
    import {pipe} from 'core/utils';
    import {loc} from 'shared/utils/pipes';
    import {FOND_INSTALLATION_LOGBOOK} from 'src/features/erp-sutor/sutor-fonds';
    import LogbookReports from './LogbookReports.svelte';
    import KpHeading from 'shared/components/kp-heading/KpHeading.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import GridFieldValue from 'shared/ui-widgets/grid/GridFieldValue.svelte';
    import KpSearchManagerProxyContextProvider from 'src/features/search/kp-search-context/KpSearchManagerProxyContextProvider.svelte';

    export let openedLogbook: RecordRow;

    const context = getProjectLogbookPairingContext();
    const logbookDateField = getFirstFieldByFieldTypeIdRecursive(openedLogbook, FOND_INSTALLATION_LOGBOOK.dateFieldId);
    const logbookCustomerProjectField = getFirstFieldByFieldTypeIdRecursive(openedLogbook, FOND_INSTALLATION_LOGBOOK.customerProjectFieldId);

    let pairedReportsSearchManagerProxy: SearchManagerConnectableProxy<RecordRow>;
    let unpairedReportsSearchManagerProxy: SearchManagerConnectableProxy<RecordRow>;

    const handleBackClick = () => {
        context.setOpenedLogbook(null);
    };

    const handlePairUnpair = async (event: CustomEvent<PairUnpairEventData>) => {
        context.addProcessedReport(event.detail.record.id);

        const updatedReport = await context.service.pairReportWithInstallationLogbook(event.detail.record, event.detail.pairingType === 'pair' ? openedLogbook.id : null);

        if (!exists(updatedReport)) {
            return;
        }

        const pairedPagination = pairedReportsSearchManagerProxy.getPagination();
        const unpairedPagination = unpairedReportsSearchManagerProxy.getPagination();

        if (event.detail.pairingType === 'pair') {
            pairedPagination.insertItemAtStart(updatedReport);
            unpairedPagination.removeItems((item) => item.id === updatedReport.id);
        } else {
            pairedPagination.removeItems((item) => item.id === updatedReport.id);
            unpairedPagination.insertItemAtStart(updatedReport);
        }

        context.removeProcessedReport(updatedReport.id);
    };
</script>

<ErpTwoSplitLayout orientation="horizontal"
                   pageClass="sutor-logbook-pairing"
                   ratios={[1, 1]}
                   resizeDisabled>

    <svelte:fragment slot="topbar">
        <KpGenericTopbar>
            <KpButton on:click={handleBackClick}>
                <IconedContent icon="arrow-left">
                    Zpět/Vybrat jiný montážní deník
                </IconedContent>
            </KpButton>

            <span>Párování výkazů k <span class="logbook-name">{openedLogbook.name}</span></span>
        </KpGenericTopbar>
    </svelte:fragment>

    <svelte:fragment slot="first-panel">
        <Spacer direction="vertical" size="s"/>
        <KpHeading type="h2">Spárované výkazy</KpHeading>
        <Spacer direction="vertical" size="sm"/>
        <small class="text-muted">Tyto výkazy jsou spárovány s vybraným montážním deníkem.</small>
        <Spacer direction="vertical" size="xl"/>
        <KpSearchManagerProxyContextProvider bind:searchManagerProxy={pairedReportsSearchManagerProxy}>
            <LogbookReports logbookRecord={openedLogbook} pairingType="unpair" on:pair-unpair={handlePairUnpair}/>
        </KpSearchManagerProxyContextProvider>
    </svelte:fragment>

    <svelte:fragment slot="second-panel">
        <Spacer direction="vertical" size="s"/>
        <KpHeading type="h2">Výkazy k párování</KpHeading>
        <Spacer direction="vertical" size="sm"/>

        <small class="text-muted">Tyto výkazy jsou napárované pouze na zakázku a jejich datum vykázaní a zakázka zákazníka odpovídá datům z montážního deníku.</small>
        <Spacer direction="vertical" size="l"/>

        <Flex direction="column" gap="s">
            <span class="prefiltered-heading">Předfiltrováno pomocí:</span>

            <LabeledValue label="Datum">
                <GridFieldValue field={logbookDateField}/>
            </LabeledValue>

            <LabeledValue label="Zakázka zákazníka">
                <GridFieldValue field={logbookCustomerProjectField}/>
            </LabeledValue>

            <LabeledValue label="Nadřazená položka">
                <GridFieldValue fondId={context.projectRecord.fond.id} value={pipe(context.projectRecord, loc())} href="/#!/records/{context.projectRecord.id}"/>
            </LabeledValue>
        </Flex>

        <Spacer direction="vertical" size="xl"/>

        <KpSearchManagerProxyContextProvider bind:searchManagerProxy={unpairedReportsSearchManagerProxy}>
            <LogbookReports logbookRecord={openedLogbook} pairingType="pair" on:pair-unpair={handlePairUnpair}/>
        </KpSearchManagerProxyContextProvider>
    </svelte:fragment>
</ErpTwoSplitLayout>

<style lang="less">
    .logbook-name {
        font-weight: bold;
    }

    .prefiltered-heading {
        font-weight: 500;
    }
</style>